import { endpoints } from '@api'
import { Box, Flex, useDisclosure } from '@chakra-ui/react'
import { Loading } from '@components'
import { useQueryParams } from '@hooks'
import {
  group,
  identify,
  identifyAnonymous,
  setUserProperties,
  trackEvent
} from '@lib/clinician-tracking'
import { useStoreActions, useStoreState } from 'easy-peasy'
import { Form, Formik } from 'formik'
import { AnimatePresence, motion } from 'framer-motion'
// @ts-ignore
import Cookies from 'js-cookie'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useMutation } from 'react-query'
import { Route, Switch, useHistory, useLocation } from 'react-router-dom'
import * as Yup from 'yup'
import { useOnboardingControllerValidateEmail } from '~/clinician-api'
import { DEFAULT_PASSWORD_SCHEMA } from '../../components/passwordrules'
import { store } from '../../store'
import { AffiliateProgram } from './components/affiliate-program'
import { Email } from './components/email'
import { GetStarted } from './components/get-started'
import { Name } from './components/name'
import { Navbar } from './components/navbar'
import { NotReadyToRecord } from './components/not-ready-to-record'
import { Password } from './components/password'
import { Practice } from './components/practice'
import { ProgressBar } from './components/progress-bar'
import { TestimonialCarousel } from './components/testimonial-carousel'
import { useGrowSurf } from './hooks/useGrowSurf'
import { buildUrl } from './utils/build-url'

const MotionContainer = motion(Box)

const Image1 =
  'https://res.cloudinary.com/hellojoy/image/upload/v1737995311/onboarding/onboarding2.jpg'
const Image2 =
  'https://res.cloudinary.com/hellojoy/image/upload/v1737995189/onboarding/onboarding_ovhmel.jpg'
const images = [Image1, Image2]

const validationSchemas = {
  email: Yup.object({
    email: Yup.string().email('Invalid email').required('Email is required')
  }),
  name: Yup.object({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required')
  }),
  password: Yup.object({
    password: DEFAULT_PASSWORD_SCHEMA
  }),
  organizationName: Yup.object({
    organizationName: Yup.string().required('Practice name is required')
  })
}

export interface FormValues {
  email: string
  firstName: string
  lastName: string
  password: string
  organizationName: string
}

export const baseUrl = '/onboarding'
const LOCAL_STORAGE_HUBSPOT_BRIDGE_ID = 'hubspotBridgeId'
const GROWSURF_CAMPAIGN_ID = process.env.REACT_APP_GROWSURF_CAMPAIGN_ID
const postSignupURLs = [`${baseUrl}/get-started`, `${baseUrl}/sample-notes`]

function useRewardfulReferral() {
  const [referralId, setReferralId] = useState<string | null>(null)

  useEffect(() => {
    // @ts-ignore
    if (window?.rewardful) {
      // @ts-ignore
      window.rewardful('ready', () => {
        // @ts-ignore
        const properties = window.Rewardful
        if (properties?.referral) {
          setReferralId(properties.referral)
        }
      })
    }
  }, [])

  return referralId
}

export const SignupV2 = () => {
  const [imagesLoaded, setImagesLoaded] = useState(false)
  const [isSignupError, setIsSignupError] = useState(false)
  const [hubspotBridgeId, setHubspotBridgeId] = useState(() => {
    return localStorage.getItem(LOCAL_STORAGE_HUBSPOT_BRIDGE_ID) || null
  })

  const [previousPaths, setPreviousPaths] = useState<string[]>([])
  const [soloReferralId, setSoloReferralId] = useState<string | null>(null)
  const [groupReferralId, setGroupReferralId] = useState<string | null>(null)
  const [affiliateSource, setAffiliateSource] = useState<string | null>(null)
  const [rewardfulReferrerTag, setRewardfulReferrerTag] = useState<
    string | null
  >(null)

  const location = useLocation()
  const history = useHistory()
  const authLogin = useStoreActions((actions: any) => actions.auth.login)
  const authSocial = useStoreActions((actions: any) => actions.auth.authSocial)

  const { isAuthenticated } = useStoreState((state: any) => state.auth)
  const {
    wasReferred,
    referrerGrowsurfId,
    addParticipant: addGrowSurfParticipant
  } = useGrowSurf(GROWSURF_CAMPAIGN_ID)

  const identifyUser = async (userData: {
    email: string
    firstName: string
    lastName: string
  }) => {
    try {
      const traits: any = {
        email: userData.email
      }

      if (userData.firstName) traits.firstName = userData.firstName
      if (userData.lastName) traits.lastName = userData.lastName

      await identifyAnonymous(traits)
    } catch (_error) {}
  }

  const trackSignup = async (signupData: FormValues) => {
    const authState = store.getState().auth
    const user = authState.user
    const userId = user?.id
    const organizationId = user?.clinic?.organization_id

    try {
      if (userId) {
        await identify(userId, {
          email: signupData.email,
          firstName: signupData.firstName,
          lastName: signupData.lastName,
          roles: user?.roles?.map((r: any) => r.name),
          clinicId: user?.clinic_id
        })
      }

      // Then set organization properties
      if (organizationId) {
        await group(organizationId, {
          organizationName: signupData.organizationName
        })
      }

      // Finally, track the signup event with user traits properly set
      trackEvent('Clinician Signed Up', {
        organizationName: signupData.organizationName,
        organizationId
      })
    } catch (error) {
      console.error('Failed to track signup with user identification:', error)
    }
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const soloParam = searchParams.get('solo')
    const groupParam = searchParams.get('group')
    const affiliateParam = searchParams.get('affiliate')
    const rewardfulReferrerTag = searchParams.get('via')

    if (soloParam) setSoloReferralId(soloParam)
    if (groupParam) setGroupReferralId(groupParam)
    if (affiliateParam) setAffiliateSource(affiliateParam)
    if (rewardfulReferrerTag) setRewardfulReferrerTag(rewardfulReferrerTag)
  }, [location.search])

  const { mutate: validateEmail, isLoading: isValidatingEmail } =
    useOnboardingControllerValidateEmail()

  const rewardfulReferralId = useRewardfulReferral()

  useEffect(() => {
    // preload the images before rendering to prevent flashing
    let loadedCount = 0
    images.forEach(src => {
      const img = new Image()
      img.src = src
      img.onload = () => {
        loadedCount++
        if (loadedCount === images.length) {
          setImagesLoaded(true)
        }
      }
      img.onerror = () => {
        loadedCount++
        if (loadedCount === images.length) {
          setImagesLoaded(true)
        }
      }
    })
  }, [])

  useEffect(() => {
    setPreviousPaths(prev => [...prev, location.pathname])
  }, [location.pathname])

  const { mutateAsync: createAccount, isLoading: isSigningUp } = useMutation(
    endpoints.postSelfServeSignup.request,
    {
      onSuccess: async (_, variables) => {
        setIsSignupError(false)
        const credentials = {
          email: variables.data.email,
          password: variables.data.password
        }
        await authLogin(credentials)
        localStorage.removeItem(LOCAL_STORAGE_HUBSPOT_BRIDGE_ID)

        await trackSignup(variables.data)

        history.replace(`${baseUrl}/get-started`)
      },
      onError(error) {
        console.log('v2_onboarding_signup_error', error)
        setIsSignupError(true)
      }
    }
  )

  const { mutateAsync: submitHubspotTrialLead } = useMutation(
    endpoints.postHubspotTrialLead.request,
    {
      onSuccess(data: any) {
        localStorage.setItem(
          LOCAL_STORAGE_HUBSPOT_BRIDGE_ID,
          data.hubspotBridgeId
        )
        setHubspotBridgeId(data.hubspotBridgeId)
      },
      onError(error) {
        console.log('error', error)
      }
    }
  )

  useEffect(() => {
    if (
      isAuthenticated &&
      !postSignupURLs.includes(location.pathname) &&
      !isSigningUp
    ) {
      history.push('/')
    }
  }, [isAuthenticated, location.pathname, isSigningUp, history])

  const didStartFromAffiliate = () => {
    return (
      previousPaths.length > 0 && previousPaths[0] === `${baseUrl}/affiliate`
    )
  }

  if (!imagesLoaded) {
    return (
      <Flex h="100vh" align="center" justify="center">
        <Loading />
      </Flex>
    )
  }

  const getHeroImageUrl = () => {
    switch (location.pathname) {
      case `${baseUrl}/affiliate`:
        return Image2
      case baseUrl:
        return Image2
      case `${baseUrl}/name`:
        return Image2
      case `${baseUrl}/password`:
        return Image2
      case `${baseUrl}/practice`:
        return Image1
      default:
        return null
    }
  }

  const heroImageUrl = getHeroImageUrl()

  const initialValues = {
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    organizationName: ''
  }

  const getCurrentStepValidation = (pathname: string) => {
    switch (pathname) {
      case baseUrl:
        return validationSchemas.email
      case `${baseUrl}/name`:
        return validationSchemas.name
      case `${baseUrl}/password`:
        return validationSchemas.password
      case `${baseUrl}/practice`:
        return validationSchemas.organizationName
      default:
        return null
    }
  }

  const getBackLink = () => {
    switch (location.pathname) {
      case baseUrl:
        return null
      case `${baseUrl}/name`:
        return () => history.push(buildUrl({ baseUrl, newPath: '', location }))
      case `${baseUrl}/password`:
        return () =>
          history.push(buildUrl({ baseUrl, newPath: '/name', location }))
      case `${baseUrl}/practice`:
        return () =>
          history.push(buildUrl({ baseUrl, newPath: '/password', location }))
      default:
        return null
    }
  }

  const getSkipLink = () => {
    switch (location.pathname) {
      case `${baseUrl}/get-started`:
        return () => {
          trackEvent('Get Started Page Skip Clicked', {})
          history.push(
            buildUrl({ baseUrl, newPath: '/sample-notes', location })
          )
        }
      case `${baseUrl}/sample-notes`:
        return () => {
          trackEvent('Sample Notes Page Skip Clicked', {})
          history.push(buildUrl({ baseUrl, newPath: '', location }))
        }
      default:
        return null
    }
  }

  const getProgress = () => {
    switch (location.pathname) {
      case baseUrl:
        return 0
      case `${baseUrl}/name`:
        return 20
      case `${baseUrl}/password`:
        return 40
      case `${baseUrl}/practice`:
        return 60
      case `${baseUrl}/get-started`:
        return 80
      case `${baseUrl}/sample-notes`:
        return 90
      default:
        return 0
    }
  }

  const getTestimonialStep = () => {
    if (didStartFromAffiliate()) {
      switch (location.pathname) {
        case baseUrl:
          return 2
        case `${baseUrl}/affiliate`:
          return 1
        case `${baseUrl}/name`:
          return 3
        case `${baseUrl}/password`:
          return 4
        case `${baseUrl}/practice`:
          return 1
        default:
          return 1
      }
    } else {
      switch (location.pathname) {
        case baseUrl:
          return 1
        case `${baseUrl}/affiliate`:
          return 5
        case `${baseUrl}/name`:
          return 2
        case `${baseUrl}/password`:
          return 3
        case `${baseUrl}/practice`:
          return 4
        default:
          return 1
      }
    }
  }

  const handleSubmit = async (values: FormValues) => {
    let growsurfId = null

    if (wasReferred) {
      const growsurfParticipant = await addGrowSurfParticipant({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        metadata: {
          organization: values.organizationName,
          source: 'onboarding'
        }
      })
      growsurfId = growsurfParticipant?.id
    }

    let optibaseExperiment = Cookies.get('landing_page_experiments') || null

    if (optibaseExperiment) {
      try {
        const decodedExperiment = decodeURIComponent(optibaseExperiment)
        optibaseExperiment = JSON.parse(decodedExperiment)
      } catch (_error) {
        optibaseExperiment = null
      }
    }

    const ubpEnabled = localStorage.getItem('ubp_enabled')

    await createAccount({
      data: {
        ...values,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        hubspotutk: Cookies.get('hubspotutk'),
        hubspotBridgeId,
        referralId: Cookies.get('bp_referral') ?? '',
        rewardfulReferralId: rewardfulReferralId,
        facebookClickId: Cookies.get('_fbc'),
        facebookPixelId: Cookies.get('_fbp'),
        pageUri: window.location.href,
        trialVariant: 'feb_2025',
        initialProduct: 'documentation_automation',
        trialType: 'freemium',
        growsurfId: growsurfId,
        referrerGrowsurfId: referrerGrowsurfId,
        affiliateSource: rewardfulReferrerTag,
        optibaseExperiment,
        usageBasedPricingEnabled: ubpEnabled
      }
    })
  }

  const handleNextStepValidation = async ({
    validateForm,
    setTouched,
    onSuccess
  }: {
    validateForm: () => Promise<Partial<typeof initialValues>>
    values: typeof initialValues
    setTouched: (touched: Record<string, boolean>) => void
    onSuccess: () => void
  }) => {
    const errors = await validateForm()
    if (Object.keys(errors).length === 0) {
      onSuccess()
    } else {
      setTouched(
        Object.keys(errors).reduce<Record<string, boolean>>((acc, field) => {
          acc[field] = true
          return acc
        }, {})
      )
    }
  }

  const handleUpdateHubspotLead = async (values: FormValues) => {
    submitHubspotTrialLead(
      {
        data: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          hubspotBridgeId,
          hubspotutk: Cookies.get('hubspotutk'),
          referralId: Cookies.get('bp_referral') ?? '',
          facebookClickId: Cookies.get('_fbc'),
          facebookPixelId: Cookies.get('_fbp'),
          pageUri: window.location.href,
          trialVariant: 'feb_2025',
          affiliateReferrerId: rewardfulReferrerTag
        }
      },
      {
        onSuccess: data => {
          setHubspotBridgeId(data.hubspotBridgeId)
        }
      }
    )
  }

  const handleSoloClick = () => {
    trackEvent('Affiliate Program Solo Selected', {
      referralId: soloReferralId,
      affiliate: affiliateSource
    })

    window.location.href = buildUrl({
      baseUrl,
      newPath: '',
      location,
      params: { via: soloReferralId }
    })
  }

  const handleGroupClick = () => {
    trackEvent('Affiliate Program Group Selected', {
      referralId: groupReferralId,
      affiliate: affiliateSource
    })

    window.location.href = buildUrl({
      baseUrl,
      newPath: '',
      location,
      params: { via: groupReferralId }
    })
  }

  const handleOAuth = async (provider: 'google' | 'microsoft') => {
    await authSocial(provider)
  }

  return (
    <>
      <Helmet>
        <meta name="title" content="Blueprint for Therapists — Try For Free" />
        <meta
          name="description"
          content="Blueprint is the AI Assistant for Therapists — automate progress notes, draft smart treatments plans, and surface actionable insights before, during and after every session."
        />
      </Helmet>
      <Flex
        w="100%"
        h="100%"
        p={{ base: 0, md: 4 }}
        gap={{
          base: 0,
          md: location.pathname === `${baseUrl}/get-started` ? 0 : 4
        }}
        bgColor="#F6F7FE"
        _before={{
          content: '""',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0,
          animation: 'none'
        }}
        sx={{
          '@keyframes fadeIn': {
            '0%': { opacity: 0 },
            '100%': { opacity: 1 }
          }
        }}
      >
        <Flex
          h="100%"
          flexDirection="column"
          width={{ base: '100%', md: heroImageUrl ? '50%' : '100%' }}
          transition="all 500ms cubic-bezier(0.920, 0.005, 0.145, 1.000)"
          position="relative"
          data-testid="onboarding-container"
        >
          <Navbar onBack={getBackLink()} onSkip={getSkipLink()} />
          <Flex flex="1" justifyContent={{ base: 'flex-start', md: 'center' }}>
            <Formik<FormValues>
              initialValues={initialValues}
              validationSchema={getCurrentStepValidation(location.pathname)}
              onSubmit={handleSubmit}
              validateOnBlur={false}
              validateOnChange={false}
            >
              {({
                validateForm,
                values,
                setTouched,
                setFieldTouched,
                handleSubmit,
                setFieldError
              }) => (
                <Form
                  noValidate
                  style={{
                    overflow: 'hidden',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  {/* @ts-ignore */}
                  <AnimatePresence exitBeforeEnter>
                    <Switch location={location} key={location.pathname}>
                      <Route
                        exact
                        path={baseUrl}
                        render={() => (
                          <Email
                            isLoading={isValidatingEmail}
                            onNext={async () =>
                              await handleNextStepValidation({
                                validateForm,
                                values,
                                setTouched,
                                onSuccess: () => {
                                  validateEmail(
                                    {
                                      data: { email: values.email }
                                    },
                                    {
                                      onSuccess: async () => {
                                        // Identify user as soon as they enter their email
                                        await identifyUser({
                                          email: values.email,
                                          firstName: values.firstName,
                                          lastName: values.lastName
                                        })

                                        handleUpdateHubspotLead(values)
                                        history.push(
                                          buildUrl({
                                            baseUrl,
                                            newPath: '/name',
                                            location
                                          })
                                        )
                                      },
                                      onError: (_error: any) => {
                                        setFieldError(
                                          'email',
                                          'An account with this email already exists, try logging in instead'
                                        )
                                        setFieldTouched('email', true, false)
                                      }
                                    }
                                  )
                                }
                              })
                            }
                            onGoogleAuth={() => handleOAuth('google')}
                            onMicrosoftAuth={() => handleOAuth('microsoft')}
                          />
                        )}
                      />
                      <Route
                        exact
                        path={`${baseUrl}/affiliate`}
                        render={() => (
                          <AffiliateProgram
                            onSoloClick={handleSoloClick}
                            onGroupClick={handleGroupClick}
                          />
                        )}
                      />
                      <Route
                        exact
                        path={`${baseUrl}/name`}
                        render={() => (
                          <Name
                            onNext={async () =>
                              await handleNextStepValidation({
                                validateForm,
                                values,
                                setTouched,
                                onSuccess: async () => {
                                  // Identify user as soon as they have email and name
                                  await identifyUser({
                                    email: values.email,
                                    firstName: values.firstName,
                                    lastName: values.lastName
                                  })

                                  handleUpdateHubspotLead(values)
                                  history.push(
                                    buildUrl({
                                      baseUrl,
                                      newPath: '/password',
                                      location
                                    })
                                  )
                                }
                              })
                            }
                          />
                        )}
                      />
                      <Route
                        exact
                        path={`${baseUrl}/password`}
                        render={() => (
                          <Password
                            firstName={values.firstName}
                            onNext={async () =>
                              await handleNextStepValidation({
                                validateForm,
                                values,
                                setTouched,
                                onSuccess: () => {
                                  history.push(
                                    buildUrl({
                                      baseUrl,
                                      newPath: '/practice',
                                      location
                                    })
                                  )
                                }
                              })
                            }
                          />
                        )}
                      />
                      <Route
                        exact
                        path={`${baseUrl}/practice`}
                        render={() => (
                          <Practice
                            isLoading={isSigningUp}
                            isError={isSignupError}
                            onNext={() => {}}
                          />
                        )}
                      />
                      <Route
                        exact
                        path={`${baseUrl}/get-started`}
                        render={() => <GetStarted />}
                      />
                      <Route
                        exact
                        path={`${baseUrl}/sample-notes`}
                        render={() => <NotReadyToRecord />}
                      />
                    </Switch>
                  </AnimatePresence>
                </Form>
              )}
            </Formik>
          </Flex>
          <ProgressBar progress={getProgress()} />
        </Flex>
        <Box
          width={{ base: '0%', md: heroImageUrl ? '50%' : '0%' }}
          bg="gray.100"
          display="flex"
          borderRadius="8px"
          justifyContent="center"
          alignItems="center"
          overflow="hidden"
          position="relative"
          data-testid="hero-image-container"
        >
          {heroImageUrl ? (
            /* @ts-ignore */
            <AnimatePresence>
              <MotionContainer
                key={heroImageUrl}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: { duration: 0.5, ease: 'easeInOut' },
                  exit: { duration: 0.1, ease: 'easeInOut' }
                }}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  background: `url(${heroImageUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              ></MotionContainer>
            </AnimatePresence>
          ) : null}

          <Box
            w="100%"
            position="absolute"
            top="50%;"
            transform="translateY(-50%)"
          >
            <TestimonialCarousel progress={getTestimonialStep()} />
          </Box>
        </Box>
      </Flex>
    </>
  )
}
